import {Injectable, Injector, OnDestroy} from '@angular/core';
import {BehaviorSubject} from 'rxjs';
import {UntypedFormBuilder, UntypedFormGroup} from '@angular/forms';
import {DynamicFormConfig} from './dynamic-form.config';
import {FieldTypeToControlBuilder} from '../dynamic-field/field-type-to-control-builder.type';
import {defaultControlBuilder} from '../dynamic-field/default-control-builder';
import {FieldConfig} from '../dynamic-field/field.config';
import {defaultControlUpdater} from '../dynamic-field/default-control-updater';
import {FieldTypeToControlUpdater} from '../dynamic-field/field-type-to-control-updater.type';
import {FieldConfigWrapper} from '../dynamic-field/field-config-wrapper';
import {DynamicFormAutofillService} from '../autofill/dynamic-form-autofill.service';
import {AsyncPipe} from '@angular/common';

@Injectable()
export class DynamicFormService implements OnDestroy {

    private formGroupSubject = new BehaviorSubject(new UntypedFormGroup({}));
    public formGroup$ = this.formGroupSubject.asObservable();

    private fieldWrappersSubject = new BehaviorSubject<FieldConfigWrapper<any>[]>([]);
    public fieldWrappers$ = this.fieldWrappersSubject.asObservable();

    private fieldConfigWrappers = new Map<string, FieldConfigWrapper<any>>();

    private controlUpdaters: FieldTypeToControlUpdater;

    constructor(
        private readonly formBuilder: UntypedFormBuilder,
        private readonly injector: Injector,
        private readonly autofillService: DynamicFormAutofillService,
        private readonly asyncPipe: AsyncPipe
    ) {
    }

    public get formGroup(): UntypedFormGroup {
        return this.formGroupSubject.value;
    }

    public get fieldWrappers(): FieldConfigWrapper<any>[] {
        return this.fieldWrappersSubject.value;
    }

    public createForm(
        formConfig: DynamicFormConfig,
        controlBuilders: FieldTypeToControlBuilder,
        controlUpdaters: FieldTypeToControlUpdater
    ) {
        this.controlUpdaters = controlUpdaters;

        const formGroup = this.formBuilder.group({});
        if (!formConfig || !formConfig.fields) {
            return;
        }

        const fieldWrappers = [];
        formConfig.fields.forEach(fieldConfig => {
            const fieldWrapper = new FieldConfigWrapper({...fieldConfig});
            this.fieldConfigWrappers.set(fieldConfig.name, fieldWrapper);
            fieldWrappers.push(fieldWrapper);

            this.initFieldConfig(fieldWrapper, formGroup, controlBuilders);
        });

        this.formGroupSubject.next(formGroup);
        this.fieldWrappersSubject.next(fieldWrappers);
    }

    public getFormValue() {
        return this.formGroupSubject.value;
    }

    public getFieldConfig(name: string): FieldConfig<FieldConfig<any>> {
        if (this.fieldConfigWrappers.has(name)) {
            return this.fieldConfigWrappers.get(name).config;
        }
        console.error('Config with that name is not found');

    }

    public async autoFill() {
        await this.autofillService.autoFilterSelectValue(this);

    }

    public setFormValue(value) {
        this.formGroup.patchValue(value);
    }

    public getFieldValue(fieldName: string) {
        return this.formGroup.get(fieldName).value;
    }

    public updateFieldValue(fieldName: string, value: any, emitEvent?: boolean) {
        this.formGroup.patchValue({[fieldName]: value}, {emitEvent});
    }

    public updateFieldConfig(
        updateFieldConfig: Partial<FieldConfig<any>>
    ) {
        const control = this.formGroup.get(updateFieldConfig.name);
        if (!control) {
            throw new Error(
                `Control with name '${updateFieldConfig.name}' doesn't exist.`
            );
        }

        const fieldConfigWrapper = this.fieldConfigWrappers.get(updateFieldConfig.name);
        if (!fieldConfigWrapper) {
            throw new Error(
                `Component with field config name '${updateFieldConfig.name}' doesn't exist.`
            );
        }

        let controlUpdater = this.controlUpdaters.get(fieldConfigWrapper.config.type);
        if (!controlUpdater) {
            controlUpdater = defaultControlUpdater;
        }

        controlUpdater(
            fieldConfigWrapper.config,
            updateFieldConfig,
            control
        );
        fieldConfigWrapper.updateFieldConfig(updateFieldConfig);
    }

    /**
     * A lifecycle hook that is called when a directive, pipe, or service is destroyed.
     */
    public ngOnDestroy() {
    }

    private initFieldConfig(fieldConfigWrapper: FieldConfigWrapper<any>, formGroup, controlBuilders: FieldTypeToControlBuilder) {
        let controlBuilder = controlBuilders.get(fieldConfigWrapper.config.type);
        if (!controlBuilder) {
            controlBuilder = defaultControlBuilder;
        }

        const builtControl = controlBuilder(
            fieldConfigWrapper.config,
            this.formBuilder,
            this.injector,
        );

        formGroup.addControl(fieldConfigWrapper.config.name, builtControl);
    }
}
